/* Scroll-driven image rotation and smoothed timeline reveals share one animation loop. */
if (!customElements.get('our-story-timeline')) {
  customElements.define('our-story-timeline', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.our-story-timeline__rail');
      this.fill = this.querySelector('.our-story-timeline__fill');
      this.items = [...this.querySelectorAll('.our-story-timeline__item')];
      if (!this.rail || !this.fill || !this.items.length) return;
      this.figures = this.items.map(item => item.querySelector('.our-story-timeline__figure'));

      this.progress = null;
      this.lastFrameTime = null;

      this.media = window.matchMedia('(prefers-reduced-motion: no-preference)');
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      this.abortController = new AbortController();
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(time => this.update(time));
      };
      this.invalidate = () => {
        this.needsMeasure = true;
        this.schedule();
      };
      this.configure = () => {
        this.configureFlip();
        if (this.desktopMedia.matches) this.items[0].classList.add('is-reached');
        this.classList.toggle('is-animated', this.media.matches);
        this.invalidate();
      };

      const { signal } = this.abortController;
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.invalidate, { passive: true, signal });
      window.addEventListener('pageshow', this.invalidate, { signal });
      this.media.addEventListener('change', this.configure, { signal });
      this.desktopMedia.addEventListener('change', this.configure, { signal });
      // Observing the document also catches layout shifts above this section.
      this.resizeObserver = new ResizeObserver(this.invalidate);
      this.resizeObserver.observe(document.body);
      this.resizeObserver.observe(this);
      this.configure();
    }

    configureFlip() {
      const enabled = ['sticky_flip', 'sticky_flip_left'].includes(this.dataset.desktopLayout)
        && this.desktopMedia.matches && this.figures.every(Boolean);
      if (enabled === this.flipEnabled) return;
      this.flipEnabled = enabled;
      this.activeIndex = null;
      this.progress = null;
      this.lastFrameTime = null;
      this.classList.toggle('is-scroll-flip', enabled);

      if (enabled) {
        this.stage = document.createElement('div');
        this.stage.className = 'our-story-timeline__stage';
        this.sticky = document.createElement('div');
        this.sticky.className = 'our-story-timeline__sticky';
        this.card = document.createElement('div');
        this.card.className = 'our-story-timeline__flip';
        // Reuse the actual figures so mobile, alt text and custom artwork stay in sync.
        this.figures.forEach((figure, index) => {
          figure.hidden = index !== 0;
          figure.querySelector('img').loading = 'eager';
          this.card.append(figure);
        });
        this.sticky.append(this.card);
        this.stage.append(this.sticky);
        this.querySelector('.our-story-timeline__inner').append(this.stage);
        this.resizeObserver.observe(this.sticky);
      } else {
        this.restoreFigures();
      }
    }

    restoreFigures() {
      this.figures?.forEach((figure, index) => {
        if (!figure) return;
        figure.hidden = false;
        this.items[index].append(figure);
        this.items[index].classList.remove('is-active');
        this.items[index].style.removeProperty('--story-content-opacity');
        this.items[index].style.removeProperty('--story-step-emphasis');
        this.items[index].style.removeProperty('--story-line-progress');
      });
      if (this.sticky) this.resizeObserver?.unobserve(this.sticky);
      this.stage?.remove();
      this.stage = null;
      this.sticky = null;
      this.card = null;
    }

    measureFlip() {
      const scrollTop = window.scrollY;
      const list = this.items[0].parentElement.getBoundingClientRect();
      const halfImageHeight = this.sticky.getBoundingClientRect().height / 2;
      this.focusPoints = this.items.map(item => {
        const year = item.querySelector('.our-story-timeline__year').getBoundingClientRect();
        const copy = item.querySelector('.our-story-timeline__copy').getBoundingClientRect();
        // Include the date, heading and body, but exclude the gap before the next step.
        return scrollTop + (year.top + copy.bottom) / 2;
      });
      // Let the sticky image enter and leave alongside the first/last text center.
      // These margins affect only the image column, preserving the text spacing.
      this.stage.style.marginTop = `${this.focusPoints[0] - scrollTop - list.top - halfImageHeight}px`;
      this.stage.style.marginBottom = `${scrollTop + list.bottom - this.focusPoints[this.focusPoints.length - 1] - halfImageHeight}px`;
    }

    getScrollPosition(cursor) {
      let position = 0;
      let start = this.focusPoints[0];
      for (let index = 1; index < this.focusPoints.length; index++) {
        const end = this.focusPoints[index];
        if (cursor <= start) break;
        if (cursor >= end) {
          position = index;
          start = end;
          continue;
        }
        // Keep the image more frontal early, then turn further per scroll pixel
        // as the next text center approaches. Reversing scroll retraces this curve.
        const progress = (cursor - start) / (end - start);
        return index - 1 + progress * progress;
      }
      return position;
    }

    updateFlip(cursor) {
      // Full text emphasis and a front-facing image share the same alignment point.
      const focus = this.getScrollPosition(cursor);
      const active = Math.round(focus);
      // Wrap edge-on so the incoming image is never mirrored.
      const angle = this.media.matches ? 180 * (focus - active) : 0;

      if (active !== this.activeIndex) {
        this.figures.forEach((figure, index) => { figure.hidden = index !== active; });
        this.items.forEach((item, index) => item.classList.toggle('is-active', index === active));
        this.activeIndex = active;
      }
      this.card.style.transform = `rotateY(${angle}deg)`;

      if (this.media.matches) {
        this.items.forEach((item, index) => {
          // Crossfade the text, marker and its outgoing line segment together.
          // Preserve each element's color while fading distant steps together.
          const weight = Math.max(0, 1 - Math.abs(index - focus));
          const eased = weight * weight * (3 - 2 * weight);
          item.style.setProperty('--story-content-opacity', (0.18 + 0.82 * eased).toFixed(3));
          item.style.setProperty('--story-step-emphasis', eased.toFixed(3));
        });
      }
    }

    update(time) {
      this.frame = null;
      if (!this.isConnected) return;

      if (this.needsMeasure) {
        const railRect = this.rail.getBoundingClientRect();
        // Snap to the correct position when the responsive layout changes.
        if (this.start !== railRect.top + window.scrollY || this.length !== railRect.height) {
          this.progress = null;
          this.lastFrameTime = null;
        }
        this.start = railRect.top + window.scrollY;
        this.length = railRect.height;
        this.milestones = this.items.map(item => {
          const marker = item.querySelector('.our-story-timeline__marker').getBoundingClientRect();
          return marker.top + window.scrollY + marker.height / 2;
        });
        if (this.flipEnabled) this.measureFlip();
        this.needsMeasure = false;
      }

      const cursor = window.scrollY + window.innerHeight * 0.5;
      // Read the actual sticky center, including the header offset and section edges.
      const stickyRect = this.flipEnabled ? this.sticky.getBoundingClientRect() : null;
      const flipCursor = stickyRect ? window.scrollY + stickyRect.top + stickyRect.height / 2 : cursor;
      if (!this.media.matches) {
        this.fill.style.transform = 'scaleY(1)';
        this.progress = null;
        this.lastFrameTime = null;
        this.items.forEach(item => item.classList.add('is-reached'));
        if (this.flipEnabled) this.updateFlip(flipCursor);
        return;
      }

      const target = this.length > 0 ? Math.min(1, Math.max(0, (cursor - this.start) / this.length)) : 0;
      const elapsed = this.lastFrameTime === null ? 1000 / 60 : Math.min(64, time - this.lastFrameTime);
      // Time-based damping feels the same at 60 Hz and 120 Hz. Stop at a quarter pixel.
      this.progress = this.progress === null ? target : this.progress + (target - this.progress) * (1 - Math.exp(-elapsed / 80));
      const settled = Math.abs(target - this.progress) * this.length < 0.25;
      if (settled) this.progress = target;

      // Write directly to the fill, avoiding an inherited CSS variable on the whole story.
      this.fill.style.transform = `scaleY(${this.progress})`;
      const filledTo = this.start + this.progress * this.length;
      // Keep the image locked to scroll even while the rail's damping settles.
      if (this.flipEnabled) {
        this.updateFlip(flipCursor);
        this.items.forEach((item, index) => {
          // Keep the advancing fill independent from each segment's fade-out.
          const start = this.milestones[index];
          const end = this.milestones[index + 1] ?? this.start + this.length;
          const progress = Math.min(1, Math.max(0, (filledTo - start) / Math.max(1, end - start)));
          item.style.setProperty('--story-line-progress', `${(progress * 100).toFixed(3)}%`);
        });
      }
      let reachedIndex = -1;
      this.items.forEach((item, index) => {
        const reached = index === 0
          ? this.desktopMedia.matches || cursor >= this.milestones[index]
          : filledTo >= this.milestones[index];
        item.classList.toggle('is-reached', reached);
        if (reached) reachedIndex = index;
      });
      // Alternating and mobile layouts pulse the latest marker reached by the fill.
      if (!this.flipEnabled && reachedIndex !== this.activeIndex) {
        this.items.forEach((item, index) => item.classList.toggle('is-active', index === reachedIndex));
        this.activeIndex = reachedIndex;
      }

      this.lastFrameTime = settled ? null : time;
      if (!settled) this.schedule();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.restoreFigures();
      this.flipEnabled = false;
      this.classList.remove('is-scroll-flip');
    }
  });
}
